import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

type CourseItem = {
  id: number;
  category_id?: number | null;
  category: string;
  name: string;
  instructor: string;
  department: string;
  assigned_user: string;
  status: "published" | "private";
  students: number;
  thumbnail?: string | null;
  video_url?: string | null;
  ppt_url?: string | null;
  ppt_preview_url?: string | null;
  pdf_url?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  certificate_enabled?: boolean;
  reward_enabled?: boolean;
};

function SubCoursesPage() {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();

  const categoryTitle =
    searchParams.get("title") || "メイン見出し";

  const [courses, setCourses] = useState<CourseItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [openMenuCourseId, setOpenMenuCourseId] = useState<
    number | null
  >(null);

  const fetchCourses = useCallback(async () => {
    const response = await fetch(
      "/courses/"
    );

    if (!response.ok) {
      throw new Error("Failed to fetch courses");
    }

    const data: CourseItem[] = await response.json();
    setCourses(data);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await fetchCourses();
      } catch (error) {
        console.error(
          "Error loading sub courses:",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [fetchCourses]);

  const subCourses = useMemo(() => {
    return courses.filter(
      (course) =>
        Number(course.category_id) === Number(categoryId)
    );
  }, [courses, categoryId]);

  const getFallbackThumbnailUrl = (
    course: CourseItem
  ) => {
    const text =
      `${course.name || ""} ${
        course.category || ""
      }`.toLowerCase();

    if (
      text.includes("react") ||
      text.includes("javascript") ||
      text.includes("typescript") ||
      text.includes("programming") ||
      text.includes("code") ||
      text.includes("web") ||
      text.includes("frontend") ||
      text.includes("backend") ||
      text.includes("system")
    ) {
      return "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80";
    }

    if (
      text.includes("security") ||
      text.includes("cyber") ||
      text.includes("password") ||
      text.includes("network") ||
      text.includes("privacy") ||
      text.includes("セキュリティ")
    ) {
      return "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=900&q=80";
    }

    if (
      text.includes("business") ||
      text.includes("management") ||
      text.includes("leader") ||
      text.includes("sales") ||
      text.includes("meeting") ||
      text.includes("office") ||
      text.includes("マネジメント") ||
      text.includes("営業")
    ) {
      return "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=900&q=80";
    }

    if (
      text.includes("training") ||
      text.includes("learning") ||
      text.includes("education") ||
      text.includes("lesson") ||
      text.includes("course") ||
      text.includes("研修") ||
      text.includes("教育") ||
      text.includes("学習")
    ) {
      return "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=900&q=80";
    }

    if (
      text.includes("health") ||
      text.includes("medical") ||
      text.includes("clinic") ||
      text.includes("beauty") ||
      text.includes("care") ||
      text.includes("健康") ||
      text.includes("美容") ||
      text.includes("医療")
    ) {
      return "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=900&q=80";
    }

    const fallbackThumbnails = [
      "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=900&q=80",
    ];

    const index =
      Math.abs(course.id) %
      fallbackThumbnails.length;

    return fallbackThumbnails[index];
  };

  const getThumbnailUrl = (course: CourseItem) => {
    if (!course.thumbnail) {
      return getFallbackThumbnailUrl(course);
    }

    if (course.thumbnail.startsWith("http")) {
      return course.thumbnail;
    }

    return `${course.thumbnail}`;
  };

  const getStatusLabel = (
    status: CourseItem["status"]
  ) => {
    return status === "published" ? "公開" : "非公開";
  };

  const getStatusBadgeStyle = (
    status: CourseItem["status"]
  ): React.CSSProperties => {
    if (status === "published") {
      return {
        backgroundColor: "#dcfce7",
        color: "#15803d",
      };
    }

    return {
      backgroundColor: "#f3f4f6",
      color: "#4b5563",
    };
  };

  const handleDeleteCourse = async (
    courseId: number
  ) => {
    const isConfirmed = window.confirm(
      "このサブコースを削除してもよろしいですか？"
    );

    if (!isConfirmed) return;

    try {
      const response = await fetch(
        `/courses/${courseId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error(
          "Failed to delete sub course"
        );
      }

      setOpenMenuCourseId(null);
      await fetchCourses();
    } catch (error) {
      console.error(
        "Error deleting sub course:",
        error
      );
      alert("サブコースの削除に失敗しました。");
    }
  };

  const handleCreateTest = (course: CourseItem) => {
    setOpenMenuCourseId(null);

    navigate(
      `/tests?courseName=${encodeURIComponent(
        course.name
      )}&category=${encodeURIComponent(
        course.category
      )}`
    );
  };

  if (loading) {
    return (
      <div style={loadingStyle}>読み込み中...</div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={pageHeaderStyle}>
        <div>
          <h1 style={pageTitleStyle}>
            {categoryTitle}
          </h1>

          <p style={pageSubtitleStyle}>
            このメイン見出しに登録されているサブコースを管理します
          </p>
        </div>

        <div style={headerButtonAreaStyle}>
          <button
            type="button"
            onClick={() => navigate("/courses")}
            style={cancelButtonStyle}
          >
            ← コース管理へ戻る
          </button>
        </div>
      </div>

      <div style={mainCardStyle}>
        <div style={sectionHeaderStyle}>
          <h2 style={sectionTitleStyle}>
            サブコース一覧
          </h2>

          <span style={countBadgeStyle}>
            {subCourses.length} 件
          </span>
        </div>

        {subCourses.length === 0 ? (
          <div style={emptyBoxStyle}>
            このメイン見出しにはまだサブコースがありません。
          </div>
        ) : (
          <div style={courseGridStyle}>
            {subCourses.map((course) => (
              <div
                key={course.id}
                style={courseCardStyle}
              >
                <div style={thumbnailWrapStyle}>
                  <img
                    src={getThumbnailUrl(course)}
                    alt={course.name}
                    style={thumbnailStyle}
                  />

                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();

                      setOpenMenuCourseId(
                        openMenuCourseId === course.id
                          ? null
                          : course.id
                      );
                    }}
                    style={threeDotButtonStyle}
                  >
                    ⋮
                  </button>

                  {openMenuCourseId === course.id && (
                    <div
                      style={courseMenuStyle}
                      onClick={(event) =>
                        event.stopPropagation()
                      }
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setOpenMenuCourseId(null);

                          navigate(
                            `/courses/edit/${course.id}`
                          );
                        }}
                        style={courseMenuItemStyle}
                      >
                        編集
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          handleDeleteCourse(course.id)
                        }
                        style={courseMenuItemStyle}
                      >
                        削除
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          handleCreateTest(course)
                        }
                        style={courseMenuItemStyle}
                      >
                        テスト作成
                      </button>
                    </div>
                  )}
                </div>

                <div style={courseBodyStyle}>
                  <div style={courseTopRowStyle}>
                    <h3 style={courseTitleStyle}>
                      {course.name}
                    </h3>

                    <span
                      style={{
                        ...getStatusBadgeStyle(
                          course.status
                        ),
                        padding: "7px 12px",
                        borderRadius: "999px",
                        fontSize: "13px",
                        fontWeight: 800,
                      }}
                    >
                      {getStatusLabel(course.status)}
                    </span>
                  </div>

                  <p style={courseMetaStyle}>
                    講師：{course.instructor || "-"}
                  </p>

                  <p style={courseMetaStyle}>
                    期間：{course.start_date || "-"} ～{" "}
                    {course.end_date || "-"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const loadingStyle: React.CSSProperties = {
  padding: "40px",
  fontSize: "20px",
  fontWeight: 700,
};

const pageStyle: React.CSSProperties = {
  padding: "40px",
  backgroundColor: "#f3f5f7",
  minHeight: "100vh",
};

const pageHeaderStyle: React.CSSProperties = {
  marginBottom: "24px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap",
};

const pageTitleStyle: React.CSSProperties = {
  fontSize: "48px",
  fontWeight: 900,
  color: "#07152f",
  margin: 0,
};

const pageSubtitleStyle: React.CSSProperties = {
  marginTop: "10px",
  color: "#64748b",
  fontSize: "22px",
};

const headerButtonAreaStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  alignItems: "center",
  flexWrap: "wrap",
};

const cancelButtonStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  backgroundColor: "#ffffff",
  color: "#07152f",
  padding: "12px 18px",
  borderRadius: "14px",
  fontWeight: 700,
  cursor: "pointer",
};

const mainCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "24px",
  padding: "28px",
  boxShadow:
    "0 10px 30px rgba(15, 23, 42, 0.05)",
};

const sectionHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "24px",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  color: "#07152f",
  fontSize: "24px",
  fontWeight: 900,
};

const countBadgeStyle: React.CSSProperties = {
  backgroundColor: "#ccfbf1",
  color: "#0f766e",
  padding: "8px 14px",
  borderRadius: "999px",
  fontSize: "14px",
  fontWeight: 800,
};

const emptyBoxStyle: React.CSSProperties = {
  padding: "36px 20px",
  borderRadius: "18px",
  backgroundColor: "#f8fafc",
  border: "1px dashed #cbd5e1",
  color: "#64748b",
  fontSize: "15px",
  fontWeight: 700,
  textAlign: "center",
};

const courseGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fill, minmax(320px, 1fr))",
  gap: "20px",
};

const courseCardStyle: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: "20px",
  backgroundColor: "#ffffff",
  overflow: "hidden",
  boxShadow:
    "0 8px 22px rgba(15, 23, 42, 0.05)",
};

const thumbnailWrapStyle: React.CSSProperties = {
  position: "relative",
  height: "180px",
  backgroundColor: "#e2e8f0",
  overflow: "visible",
};

const thumbnailStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};

const courseBodyStyle: React.CSSProperties = {
  padding: "20px",
};

const courseTopRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  alignItems: "flex-start",
};

const courseTitleStyle: React.CSSProperties = {
  margin: 0,
  color: "#07152f",
  fontSize: "22px",
  fontWeight: 900,
};

const courseMetaStyle: React.CSSProperties = {
  marginTop: "10px",
  marginBottom: 0,
  color: "#64748b",
  fontSize: "15px",
  fontWeight: 600,
};

const threeDotButtonStyle: React.CSSProperties = {
  position: "absolute",
  top: "12px",
  right: "12px",
  width: "36px",
  height: "36px",
  borderRadius: "999px",
  border: "none",
  backgroundColor: "#ffffff",
  color: "#07152f",
  fontSize: "22px",
  fontWeight: 900,
  cursor: "pointer",
  boxShadow:
    "0 8px 18px rgba(15, 23, 42, 0.18)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
};

const courseMenuStyle: React.CSSProperties = {
  position: "absolute",
  top: "54px",
  right: "12px",
  zIndex: 20,
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  boxShadow:
    "0 12px 28px rgba(15, 23, 42, 0.18)",
  padding: "8px",
  minWidth: "150px",
};

const courseMenuItemStyle: React.CSSProperties = {
  width: "100%",
  border: "none",
  backgroundColor: "#ffffff",
  color: "#07152f",
  padding: "10px 12px",
  borderRadius: "10px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer",
  textAlign: "left",
};

export default SubCoursesPage;